/**
 * circularRail.tsx
 */
import AIIcon from '@components/atoms/icons/AI';
import ArrowsSelector from '../nav/arrowsSelector';
import Event from '@shared/classes/event';
import Focus from '@shared/types/enums/focus';
import MainContext from '@shared/components/utils/context/mainContext';
import Rail from '@shared/classes/rail';
import RailItemContainer from '@components/atoms/container/railItemContainer';
import RailTitle from '@components/atoms/text/railTitle';
import useCard from '@components/sections/rails/lib/useCard';
import useRail from '../../lib/useRail';
import useStableSelectHandler from '../../lib/useStableSelectHandler';
import { memo, useContext } from 'react';

/**
 * Scale of the AI icon.
 */
const AI_ICON_SCALE = 0.8;
/**
 * Maximum number of items to display in the circular rail.
 */
const MAX_CIRCULAR_ITEMS = 15;

/**
 * Circular rail component. Displays a rail with a title and a list of items that can be infinitely scrolled horizontally.
 * Memoized on `rail` identity so navigating within one rail doesn't
 * re-render every other rail on the page.
 */
function CircularRail({ rail }: { rail: Rail }) {
  const { Card } = useCard(rail);
  const { config, selectAsset } = useContext(MainContext);
  const {
    focused,
    hovered,
    hIndex,
    vIndex,
    currentRail,
    callbacks: railCallbacks,
  } = useRail(rail.id, Focus.RAILS_LIST);
  const getClickHandler = useStableSelectHandler(rail.id, selectAsset);
  const renderRail = vIndex <= currentRail + config.threshold.front;

  return renderRail ? (
    <RailItemContainer hideRail={currentRail > vIndex} {...railCallbacks}>
      {rail.title ? (
        <RailTitle>
          {rail.reelIA ? <AIIcon scale={AI_ICON_SCALE} /> : ''}
          {rail.title}
        </RailTitle>
      ) : null}
      <div style={{ display: 'flex' }}>
        {[...(rail.data as Event[]), ...(rail.data as Event[])]?.map(
          (item, idx) => {
            if (!item) return null;
            const display = Math.abs(idx - hIndex) < MAX_CIRCULAR_ITEMS;
            const hideItem = hIndex > idx;
            return display ? (
              <Card
                key={`${item?.id}-${idx}`}
                item={item}
                hide={hideItem}
                focused={focused && hIndex === idx}
                onClick={getClickHandler(idx)}
              />
            ) : null;
          }
        )}
      </div>
      <ArrowsSelector hovered={hovered} hIndex={hIndex} rail={rail} />
    </RailItemContainer>
  ) : null;
}

export default memo(
  CircularRail,
  // See defaultRail.tsx: `useFilter` mutates `rail.data` in place, so it
  // must be compared explicitly or a circular event rail never re-renders
  // when the filter changes it.
  (prev, next) => prev.rail === next.rail && prev.rail.data === next.rail.data
);
